import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget.
 *
 * The site key is read from a meta tag that Django renders, not from a build
 * time constant, because the key is bound to a hostname and the same bundle is
 * served on production, dev and localhost. No meta tag means no key configured,
 * and the component renders nothing so signup still works untouched.
 */

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

type TurnstileApi = {
    render: (el: HTMLElement, opts: Record<string, unknown>) => string
    reset: (id?: string) => void
    remove: (id?: string) => void
}

declare global {
    interface Window {
        turnstile?: TurnstileApi
    }
}

export const turnstileSiteKey = (): string =>
    document.querySelector<HTMLMetaElement>('meta[name="turnstile-site-key"]')?.content?.trim() || ''

let scriptPromise: Promise<void> | undefined

/** Loaded once per page and shared, so two widgets cannot race the same script tag. */
const loadTurnstileScript = (): Promise<void> => {
    if (scriptPromise) return scriptPromise
    if (window.turnstile) return Promise.resolve()

    scriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
        if (existing) {
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () => reject(new Error('turnstile script failed')))
            return
        }

        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = SCRIPT_SRC
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => {
            scriptPromise = undefined
            reject(new Error('turnstile script failed'))
        }
        document.head.appendChild(script)
    })

    return scriptPromise
}

interface TurnstileProps {
    /** Called with a solved token, or '' whenever the current token stops being usable. */
    onToken: (token: string) => void
    /** Change this to force a fresh challenge. A token is single use. */
    resetKey?: number
}

export const Turnstile = ({ onToken, resetKey = 0 }: TurnstileProps) => {
    const container = useRef<HTMLDivElement>(null)
    const widgetId = useRef<string | undefined>(undefined)

    // Held in a ref so a new inline callback on every parent render does not
    // tear down and re-render the widget, which would drop a solved token.
    const emit = useRef(onToken)
    emit.current = onToken

    const siteKey = turnstileSiteKey()

    useEffect(() => {
        if (!siteKey || !container.current) return
        let cancelled = false

        loadTurnstileScript()
            .then(() => {
                if (cancelled || !container.current || !window.turnstile) return
                widgetId.current = window.turnstile.render(container.current, {
                    sitekey: siteKey,
                    callback: (token: string) => emit.current(token),
                    'expired-callback': () => emit.current(''),
                    'timeout-callback': () => emit.current(''),
                    'error-callback': () => emit.current(''),
                    theme: 'dark',
                })
            })
            .catch(() => {
                // Cloudflare unreachable. The backend treats that as an outage
                // and lets registrations through, so leaving the token empty is
                // the correct outcome rather than a dead form.
                emit.current('')
            })

        return () => {
            cancelled = true
            if (widgetId.current && window.turnstile) {
                window.turnstile.remove(widgetId.current)
                widgetId.current = undefined
            }
        }
    }, [siteKey])

    useEffect(() => {
        if (!resetKey || !widgetId.current || !window.turnstile) return
        window.turnstile.reset(widgetId.current)
        emit.current('')
    }, [resetKey])

    if (!siteKey) return null

    return <div ref={container} className='flex justify-center my-2' />
}
